import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { MessageBubble } from "@/components/chat/MessageBubble";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { chatSessionService, guestService } from "@/lib/api";
import { formatDate, formatKrw, formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useSelectedProperty } from "@/stores/property";
import type { ChatSession, ChatSessionKind } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

function formatLastMessageAt(iso: string): string {
  return `${formatDate(iso)} ${formatTime(iso)}`;
}

const KIND_LABEL: Record<ChatSessionKind, string> = { visitor: "방문자", guest: "게스트" };

const KIND_TAG_CLASS: Record<ChatSessionKind, string> = {
  visitor: "bg-accent/15 text-accent",
  guest: "bg-primary/10 text-primary",
};

const RESERVATION_STATUS_LABEL: Record<string, string> = {
  booked: "예약",
  checked_in: "체크인",
  checked_out: "체크아웃",
  cancelled: "취소",
};

function KindTag({ kind }: { kind: ChatSessionKind }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
        KIND_TAG_CLASS[kind]
      )}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

type MetaItem = string | false | null | undefined;

/** "값1 · 값2 · …" — null/빈 항목은 건너뛴다. */
function MetaLine({ items }: { items: MetaItem[] }) {
  const shown = items.filter(Boolean);
  if (shown.length === 0) return null;
  return <p className="mt-0.5 text-xs text-muted-foreground">{shown.join(" · ")}</p>;
}

function sessionMetaItems(s: ChatSession): MetaItem[] {
  if (s.kind === "visitor") {
    return [
      s.first_seen_at && `첫 방문 ${formatLastMessageAt(s.first_seen_at)}`,
      s.last_seen_at && `최근 활동 ${formatLastMessageAt(s.last_seen_at)}`,
    ];
  }
  return [
    s.check_in && s.check_out
      ? `${formatDate(s.check_in)} ~ ${formatDate(s.check_out)}`
      : s.check_in && `체크인 ${formatDate(s.check_in)}`,
    s.room_type_name && `타입 ${s.room_type_name}`,
    s.room_name && `호실 ${s.room_name}`,
    s.reservation_status &&
      `예약 상태: ${RESERVATION_STATUS_LABEL[s.reservation_status] ?? s.reservation_status}${
        s.reservation_channel && s.reservation_channel !== "direct"
          ? ` (${s.reservation_channel})`
          : ""
      }`,
    s.reservation_total != null && formatKrw(s.reservation_total),
    s.phone,
    s.email,
  ];
}

export function ConversationsPage() {
  const propertyId = useSelectedProperty();
  const [searchParams] = useSearchParams();
  const deepGuestId = searchParams.get("guest_id");

  const [selectedId, setSelectedId] = useState<string | null>(deepGuestId);
  const [filter, setFilter] = useState<"all" | ChatSessionKind>("all");

  const sessions = useQuery({
    queryKey: ["chat-sessions", propertyId],
    queryFn: () => chatSessionService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const sessionList = sessions.data ?? [];
  const visibleSessions =
    filter === "all" ? sessionList : sessionList.filter((s) => s.kind === filter);
  const currentId = selectedId ?? visibleSessions[0]?.session_id ?? null;
  const currentSession = sessionList.find((s) => s.session_id === currentId);
  // Deep-linked guest sessions may have no messages yet → not in the list.
  const currentKind: ChatSessionKind = currentSession?.kind ?? "guest";

  const messages = useQuery({
    queryKey: ["session-messages", currentKind, currentId],
    queryFn: () => chatSessionService.messages(currentKind, currentId ?? ""),
    enabled: Boolean(currentId),
  });

  // Deep-linked guest with no messages yet: not in the session list, so fetch directly.
  const fallbackGuest = useQuery({
    queryKey: ["guest", currentId],
    queryFn: () => guestService.get(currentId ?? ""),
    enabled: Boolean(currentId) && sessions.isSuccess && !currentSession,
  });

  const paneTitle = currentSession?.name ?? fallbackGuest.data?.name;
  const paneMetaItems = currentSession
    ? sessionMetaItems(currentSession)
    : fallbackGuest.data
      ? [
          fallbackGuest.data.check_in &&
            fallbackGuest.data.check_out &&
            `${formatDate(fallbackGuest.data.check_in)} ~ ${formatDate(fallbackGuest.data.check_out)}`,
          fallbackGuest.data.room?.name && `객실 ${fallbackGuest.data.room.name}`,
          fallbackGuest.data.phone,
          fallbackGuest.data.email,
        ]
      : [];

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">컨시어지</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            챗봇 대화 내역을 세션별로 확인하세요. (읽기 전용)
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            <KindTag kind="visitor" />는 공개 챗봇 링크로 문의한 익명 사용자,{" "}
            <KindTag kind="guest" />는 예약과 연결된 투숙객입니다.
          </p>
        </div>
        <Tabs
          value={filter}
          onValueChange={(v) => {
            setFilter(v as "all" | ChatSessionKind);
            setSelectedId(null);
          }}
        >
          <TabsList>
            <TabsTrigger value="all">전체</TabsTrigger>
            <TabsTrigger value="visitor">방문자</TabsTrigger>
            <TabsTrigger value="guest">게스트</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="flex h-[calc(100vh-14rem)] min-h-[24rem] overflow-hidden p-0">
        <aside className="w-72 shrink-0 overflow-y-auto border-r">
          {sessions.isLoading ? (
            <BrandLoader className="py-12" size={36} label="불러오는 중…" />
          ) : sessions.isError ? (
            <p className="p-4 text-sm text-destructive">세션 목록을 불러오지 못했습니다.</p>
          ) : visibleSessions.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              아직 {filter === "all" ? "" : `${KIND_LABEL[filter]} `}대화가 없습니다.
            </p>
          ) : (
            <ul>
              {visibleSessions.map((s) => (
                <li key={s.session_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(s.session_id)}
                    className={cn(
                      "w-full border-b px-4 py-3 text-left transition-colors hover:bg-accent/10",
                      s.session_id === currentId && "bg-accent/15"
                    )}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{s.name}</span>
                        <KindTag kind={s.kind} />
                      </span>
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] tabular-nums text-muted-foreground">
                        {s.message_count}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-[11px] tabular-nums text-muted-foreground">
                      {formatLastMessageAt(s.last_message_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="flex flex-1 flex-col overflow-hidden">
          {currentId ? (
            <>
              <div className="border-b px-4 py-3">
                <h2 className="flex items-center gap-2 text-sm font-medium">
                  {paneTitle ? `${paneTitle}님 대화` : "대화 기록"}
                  <KindTag kind={currentKind} />
                </h2>
                <MetaLine items={paneMetaItems} />
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {messages.isLoading ? (
                  <BrandLoader className="py-12" size={44} label="불러오는 중…" />
                ) : messages.isError ? (
                  <p className="p-4 text-sm text-destructive">대화를 불러오지 못했습니다.</p>
                ) : messages.data && messages.data.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {messages.data.map((m) => (
                      <div key={m.id} className="flex flex-col gap-0.5">
                        <MessageBubble sender={m.role} content={m.content} />
                        <span
                          className={cn(
                            "px-2 text-[11px] tabular-nums text-muted-foreground",
                            m.role === "user" ? "text-right" : "ml-10"
                          )}
                        >
                          {formatTime(m.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="p-8 text-center text-sm text-muted-foreground">
                    아직 대화 기록이 없습니다.
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="m-auto p-8 text-sm text-muted-foreground">좌측에서 세션을 선택하세요.</p>
          )}
        </section>
      </Card>
    </div>
  );
}
